import { message, Table, TableColumnsType, Modal, List, Tag as AntTag } from 'antd'
import React, { useState, useEffect } from 'react'

import { Box, Button, Typography } from '@mui/material'
import { ICustomer } from '../../../../../types/customer'
import Pagination from '../../../../../components/Pagination'
import { IFilterMapped } from './CustomerReportFilter'
import { useDispatch } from 'react-redux'
import { openModal } from '../../../../../../store/reducers/modal/modal.slice'
import { CustomerBlackListFormModal } from '../../../../Customer/Features/CustomerBlackList/components/CustomerBlackListFormModal'
import dayjs from 'dayjs'
import { axiosInstance } from '../../../../../hooks/useAxios'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'
import { Tag } from '../../../../../components/Tag'
import { useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import PageUrls from '../../../../../constants/page-urls'
import { useUser } from '../../../../../hooks/useUser'
import CustomerOrdersExpand from './CustomerOrdersExpand'

interface ICustomerReportTableProps {
  data?: any[]
  isLoading?: boolean
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch?: (filter: IFilterMapped) => void
  total: number
  mutate?: any
}

const ICustomerReportTable = ({
  data,
  isLoading,
  filter,
  onFilterChange,
  onSearch,
  total,
  mutate,
}: ICustomerReportTableProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])
  const [isNamesModalOpen, setIsNamesModalOpen] = useState(false)
  const [modalNames, setModalNames] = useState<string[]>([])
  const [modalPhone, setModalPhone] = useState<string>('')

  const { merchant } = useUser()
  const openCRM = merchant?.config?.openCRM

  const onHandleDeleteCustomerBlackList = async (objectId: string) => {
    try {
      const response = await axiosInstance.delete(`/blacklists/${objectId}`)
      if (response.status === 200) {
        message.success('ลบข้อมูลสำเร็จ')
        mutate?.()
      }
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const handleNavigateToLeadList = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('กรุณาเลือกข้อมูลก่อน')
      return
    }

    const mappedData = data?.map((customer: any, index: number) => ({
      ...customer,
      key: index,
    }))

    const selectedCustomers = mappedData?.filter((customer) =>
      selectedRowKeys.includes(customer.key),
    )
    const phones = selectedCustomers
      ?.map((customer) => customer.phone)
      .filter((phone) => phone)

    if (phones && phones.length > 0) {
      const queryParams = queryString.stringify(
        { phones },
        { arrayFormat: 'bracket' },
      )
      navigate(`${PageUrls.CRM.Lead.List}?${queryParams}`)
    } else {
      message.warning('ไม่พบหมายเลขโทรศัพท์ในข้อมูลที่เลือก')
    }
  }

  // Reset selectedRowKeys when page changes
  useEffect(() => {
    setSelectedRowKeys([])
    // แถวถูก key ด้วย index หน้าใหม่จึงใช้ key ชุดเดิมแต่คนละลูกค้า
    setExpandedRowKeys([])
  }, [filter.page])

  const columns: TableColumnsType<
    ICustomer & { key: number; numberRow: number }
  > = [
      {
        title: 'ลำดับ',
        dataIndex: 'numberRow',
        key: 'numberRow',
        align: 'center' as const,
        width: 80,
        render: (numberRow: number) => {
          return (
            <Box>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign={'center'}
              >
                {numberRow}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'ชื่อ ลูกค้า',
        dataIndex: 'username',
        key: 'username',
        align: 'center' as const,
        render: (_: any, customer: any) => {
          const hasNames = customer?.names && Array.isArray(customer.names) && customer.names.length > 0

          const handleShowNames = () => {
            setModalNames(customer.names)
            setModalPhone(customer.phone)
            setIsNamesModalOpen(true)
          }

          return (
            <Box sx={{ minWidth: 100, textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {customer.name}{' '}
                  {customer?.blacklist ? <Tag color="error">blacklist</Tag> : <></>}
                </Typography>
                {hasNames && (
                  <AntTag
                    color="blue"
                    style={{ cursor: 'pointer' }}
                    onClick={handleShowNames}
                  >
                    แสดงรายชื่อ
                  </AntTag>
                )}
              </Box>
              <Typography variant={'caption'} color={'text.secondary'}>
                {customer.phone}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'จำนวนคำสั่งซื้อ',
        dataIndex: 'order',
        key: 'order',
        align: 'center' as const,
        render: (_: number, customer: any) => {
          return (
            <Box sx={{ minWidth: 100 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                textAlign={'center'}
              >
                {(customer?.order || 0).toLocaleString()}
              </Typography>
            </Box>
          )
        },
      },

      {
        title: 'จำนวนยอดซื้อ',
        dataIndex: 'subTotal',
        key: 'subTotal',
        align: 'center' as const,
        render: (_: number, customer: any) => {
          return (
            <Box sx={{ minWidth: 100 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                textAlign={'center'}
              >
                {customer?.total?.toLocaleString() || 0} บาท
              </Typography>
            </Box>
          )
        },
      },

      {
        title: 'จำนวนคำสั่งซื้อ (ตีกลับ)',
        dataIndex: 'returnedOrder',
        key: 'returnedOrder',
        align: 'center' as const,
        render: (_: number, customer: any) => {
          return (
            <Box sx={{ minWidth: 100 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                textAlign={'center'}
              >
                {(customer?.returnedOrder || 0).toLocaleString()}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'จำนวนยอดซื้อ (ตีกลับ)',
        dataIndex: 'returnedTotal',
        key: 'returnedTotal',
        align: 'center' as const,
        render: (_: number, customer: any) => {
          return (
            <Box sx={{ minWidth: 100 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                textAlign={'center'}
              >
                {customer?.returnedTotal?.toLocaleString() || 0} บาท
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'ตั้งค่า',
        dataIndex: 'setting',
        key: 'setting',
        align: 'center' as const,
        render: (_: number, customer: any) => {
          if (customer?.blacklist) {
            return (
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() =>
                  onHandleDeleteCustomerBlackList(customer?.blacklist?.objectId!)
                }
              >
                ลบออกจาก Blacklist
              </Button>
            )
          }
          return (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() =>
                dispatch(
                  openModal({
                    title: 'สร้างลูกค้า Black List',
                    content: (
                      <CustomerBlackListFormModal
                        fix={true}
                        customerBlackList={{
                          name: customer.name,
                          phoneNumber: customer.phone,
                          isActive: true,
                          createdAt: dayjs().format(),
                        }}
                        onUpdateCustomerBlackListSuccess={mutate}
                      />
                    ),
                  }),
                )
              }
            >
              ตั้งค่า Blacklist
            </Button>
          )
        },
      },
    ]

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  }

  return (
    <>
      {selectedRowKeys.length > 0 && openCRM && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNavigateToLeadList}
          >
            ค้นหาหมายเลขโทรศัพท์ใน lead
          </Button>
        </Box>
      )}
      <Table
        loading={isLoading}
        size={'middle'}
        columns={columns}
        rowSelection={openCRM ? rowSelection : undefined}
        expandable={{
          // โหลดเฉพาะแถวที่กางอยู่ ไม่ให้ยิง /order/v1 ทุกแถวที่ render
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
          rowExpandable: (customer: any) => !!customer.phone,
          expandedRowRender: (customer: any) => (
            <CustomerOrdersExpand phone={customer.phone} filter={filter} />
          ),
        }}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map((customer: any, index: number) => {
          return {
            ...customer,
            key: index,
            numberRow:
              index + 1 + ((filter.page || 1) - 1) * (filter.perPage || 10),
          }
        })}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page || 1}
        pageSize={filter.perPage || 10}
        onChange={(page, perPage) => {
          const newFilter = { ...filter, page, perPage }
          onFilterChange(newFilter)
          // Trigger search to update API call with new pagination
          if (onSearch) {
            onSearch(newFilter)
          }
        }}
      />
      <Modal
        title={`รายชื่อของลูกค้าที่ใช้หมายเลขโทรศัพท์ ${modalPhone}`}
        open={isNamesModalOpen}
        onCancel={() => setIsNamesModalOpen(false)}
        footer={null}
        width={600}
      >
        <Box sx={{ mt: 2 }}>
          <List
            bordered
            dataSource={modalNames}
            renderItem={(name: string, index: number) => (
              <List.Item>
                <Typography variant="body1" color="text.primary">
                  {index + 1}. {name}
                </Typography>
              </List.Item>
            )}
          />
        </Box>
      </Modal>
    </>
  )
}

export default ICustomerReportTable
